import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { UserRole } from '@shared/types/user';
import { checkPermissionGuard } from './guards/check-permission.guard';
import { loggedInAuthGuard } from './guards/is-logged-in.guard';
import { isNotLoggedInGuard } from './guards/is-not-logged-in.guard';
import { QUIZ_ROUTES } from '@quiz/quiz.routes';
import { WHEEL_ROUTES } from './wheel/wheel.routes';
import { SPIN_MACHINE_ROUTES } from '@spin-machine/spin-machine.routes';
import { JACKPOT_ROUTES } from '@jackpot/jackpot.routes';
import { HUB_ROUTES } from './hub/hub.routes';
import { BOARD_ROUTES } from '@board/board.routes';
import { MULTI_WHEEL_ROUTES } from './multi-wheel/multi-wheel.routes';
import { STREAKS_ROUTES } from '@streaks/streaks.routes';

const routes: Routes = [
  {
    path: '',
    redirectTo: 'quiz-games',
    pathMatch: 'full',
  },
  {
    path: 'login',
    loadComponent: () =>
      import('./generic/login/login.component').then((m) => m.LoginComponent),
    canActivate: [isNotLoggedInGuard],
  },
  {
    path: 'client',
    loadComponent: () =>
      import('./generic/client-settings/client-settings/client-settings.component').then(
        (m) => m.ClientSettingsComponent,
      ),
    canActivate: [loggedInAuthGuard, checkPermissionGuard],
    data: {
      permission: [UserRole.Admin],
    },
  },
  {
    path: 'client-bi',
    loadComponent: () =>
      import('./generic/client-bi/client-bi.component').then(
        (m) => m.ClientBiComponent,
      ),
    canActivate: [loggedInAuthGuard, checkPermissionGuard],
    data: {
      permission: [UserRole.Admin, UserRole.ClientAdmin, UserRole.Manager],
    },
  },
  {
    path: 'token-validation',
    loadChildren: () =>
      import('./generic/token-validation/token-validation.module').then(
        (m) => m.TokenValidationModule,
      ),
    canActivate: [loggedInAuthGuard, checkPermissionGuard],
    data: {
      permission: [
        UserRole.Admin,
        UserRole.ClientAdmin,
        UserRole.Manager,
        UserRole.Supervisor,
        UserRole.CustomerSupport,
      ],
    },
  },
  {
    path: 'tasks',
    loadChildren: () =>
      import('./generic/tasks/tasks.module').then((m) => m.TasksModule),
    canActivate: [loggedInAuthGuard, checkPermissionGuard],
    data: {
      permission: [UserRole.Admin],
    },
  },
  {
    path: 'labels',
    loadComponent: () =>
      import('./generic/labels/labels.component').then(
        (m) => m.LabelsComponent,
      ),
    canActivate: [loggedInAuthGuard, checkPermissionGuard],
    data: {
      permission: [UserRole.Admin, UserRole.ClientAdmin],
    },
  },
  {
    path: 'campaigns',
    loadComponent: () =>
      import('./generic/campaigns/campaigns/campaigns.component').then(
        (m) => m.CampaignsComponent,
      ),
    canActivate: [loggedInAuthGuard, checkPermissionGuard],
    data: {
      permission: [UserRole.Admin],
    },
  },
  {
    path: 'preview',
    loadComponent: () =>
      import('./generic/preview/preview.component').then(
        (m) => m.PreviewComponent,
      ),
    canActivate: [loggedInAuthGuard, checkPermissionGuard],
    data: {
      permission: [UserRole.Admin],
    },
  },
  {
    path: 'users',
    loadChildren: () =>
      import('./generic/users/users.module').then((m) => m.UsersModule),
    canActivate: [loggedInAuthGuard, checkPermissionGuard],
    data: {
      permission: [
        UserRole.Admin,
        UserRole.ClientAdmin,
        UserRole.Manager,
        UserRole.Supervisor,
        UserRole.CustomerSupport,
      ],
    },
  },
  {
    path: 'actions',
    loadChildren: () =>
      import('./generic/actions/actions.module').then((m) => m.ActionsModule),
    canActivate: [loggedInAuthGuard, checkPermissionGuard],
    data: {
      permission: [UserRole.Admin],
    },
  },
  {
    path: 'assets',
    loadChildren: () =>
      import('./generic/assets/assets.module').then((m) => m.AssetsModule),
    canActivate: [loggedInAuthGuard, checkPermissionGuard],
    data: {
      permission: [UserRole.Admin],
    },
  },
  {
    path: 'reporting',
    loadChildren: () =>
      import('./generic/reporting/reporting.module').then(
        (m) => m.ReportingModule,
      ),
    canActivate: [loggedInAuthGuard, checkPermissionGuard],
    data: {
      permission: [
        UserRole.Admin,
        UserRole.ClientAdmin,
        UserRole.Supervisor,
        UserRole.Manager,
      ],
    },
  },
  {
    path: 'prizes',
    loadChildren: () =>
      import('./generic/prize-tab/prize-tab.module').then(
        (m) => m.PrizeTabModule,
      ),
    canActivate: [loggedInAuthGuard, checkPermissionGuard],
    data: {
      permission: [
        UserRole.Admin,
        UserRole.ClientAdmin,
        UserRole.Manager,
        UserRole.Supervisor,
      ],
    },
  },
  ...QUIZ_ROUTES,
  ...WHEEL_ROUTES,
  ...SPIN_MACHINE_ROUTES,
  ...JACKPOT_ROUTES,
  ...HUB_ROUTES,
  ...BOARD_ROUTES,
  ...MULTI_WHEEL_ROUTES,
  ...STREAKS_ROUTES,
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
